import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import newsArticles, { newsCategories } from '../data/newsData';
import SEO from '../components/SEO';

const News = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = activeCategory === 'All'
    ? newsArticles
    : newsArticles.filter(a => a.category === activeCategory);

  const featured = filtered.filter(a => a.featured);
  const recent = filtered.filter(a => !a.featured);

  return (
    <div className="min-h-screen bg-white" data-testid="news-page">
      <SEO title="News & Updates" description="Latest news and updates on trademark law, copyright regulations, and intellectual property developments from Federal Trademark Experts." path="/resources/news" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-2 mb-6">
            <span className="text-sm font-semibold text-blue-200">Latest Updates</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
            Trademark News &
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300"> Updates</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Stay ahead with the latest trademark law updates, USPTO announcements, and industry developments
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="bg-white border-b border-slate-200 sticky top-[72px] z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-2 py-4 justify-center scrollbar-hide" data-testid="news-category-filters">
            {newsCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Stories */}
      {featured.length > 0 && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Featured Stories</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featured.map((article) => (
                <Link key={article.id} to={`/resources/news/${article.id}`} className="group" data-testid={`news-featured-${article.id}`}>
                  <article className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
                    <div className="relative overflow-hidden aspect-[16/9]">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-4 left-4">
                        <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">{article.category}</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                        <span>{article.date}</span>
                        <span className="text-blue-600 font-medium">{article.source}</span>
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">{article.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed mb-4">{article.excerpt}</p>
                      <span className="text-blue-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read Full Story <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent News */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {recent.length > 0 && <h2 className="text-2xl font-bold text-slate-900 mb-8">Recent News</h2>}
          <div className="flex flex-wrap justify-center gap-8" data-testid="news-grid">
            {(featured.length === 0 ? filtered : recent).map((article) => (
              <Link key={article.id} to={`/resources/news/${article.id}`} className="group w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.33%-1.5rem)]" data-testid={`news-card-${article.id}`}>
                <article className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4">
                      <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">{article.category}</span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <span>{article.date}</span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight">{article.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-1">{article.excerpt}</p>
                    <span className="text-blue-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </article>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No news articles found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default News;
